import apiConfig from '@/config/api';
import {
    BioscopeImageController_Image,
    CageImageController_Image,
    template,
    WelfareController_GetFullSizeImage,
} from '@/services/services';
import type { BioscopeImage } from '@/services/types';
import {
    extractDayInTimezone,
    formatDateTimeInTimezone,
    formatInTimeZone,
    toTimezoneISOString,
} from '@/utils/date';

import type { Image, ImageRow, LaksvelScore, MappedImage } from './types';

/** Bioscope frames are always this size; the API does not report dimensions for them. */
const BIOSCOPE_IMAGE_SIZE = { width: 968, height: 608 };

/** The fin prefixes the API puts on laksvel tags, which the UI shows as a bare score. */
const FIN_TAG_PREFIX = /^(adipose|anal|caudal|dorsal|pectoral|pelvic)/;

/**
 * Adds the fields that depend on the location's timezone.
 *
 * `day` is derived here rather than taken from the API's own `day` field, because the
 * timeline groups by the location's calendar day, not the server's. `formattedTimeStamp` is
 * worded in `language`, the page language unless given.
 */
export function withTimezoneMetadata(
    image: Omit<Image, 'day' | 'localTimeStamp' | 'formattedTimeStamp'>,
    timezone: string | undefined,
    queryParam: string | undefined,
    language?: string
): Image {
    const tz = timezone || 'UTC';

    return {
        ...image,
        day: extractDayInTimezone(image.timeStamp, tz),
        localTimeStamp: toTimezoneISOString(image.timeStamp, tz),
        formattedTimeStamp: formatDateTimeInTimezone(image.timeStamp, tz, language),
        permalink: queryParam
            ? `${window.location.origin}${window.location.pathname}?${queryParam}=${image.id}`
            : undefined,
    };
}

/**
 * The scp command an OptoScale admin uses to pull a frame's raw pickles off the bioscope, and
 * that an image feedback report hands the NAS saver. Undefined for a frame that has no raw
 * pickle file to name: without a bioscope, a fish or a readable time (black-and-white welfare
 * frames have no fish), a command would name a file that does not exist, such as
 * `..._undefined.gz`, and the backend refuses it.
 */
export function toScpCommand({
    bioscopeId,
    timeStamp,
    fishId,
}: {
    bioscopeId?: number;
    timeStamp: string;
    fishId?: number | null;
}): string | undefined {
    if (bioscopeId == null || !Number.isInteger(bioscopeId) || bioscopeId < 1) return undefined;
    // 0 is a real fish id: the welfare pipeline numbers the fish on a frame from 0.
    if (fishId == null || !Number.isInteger(fishId) || fishId < 0) return undefined;
    const folderStamp = formatInTimeZone(timeStamp, 'yyyy_MM_dd', 'UTC');
    const timeStampUtc = formatInTimeZone(timeStamp, 'yyyy_MM_dd_HH_mm_ss_SSS', 'UTC');
    if (!folderStamp || !timeStampUtc) return undefined;
    const fileStamp = `B${bioscopeId}_${timeStampUtc}_${fishId}`;
    const baseFolder = `${bioscopeId}:/store/optoscale/bioscopeData`;

    return `scp ${baseFolder}/lice/${folderStamp}/pickles/lice_crop_${fileStamp}.gz ${baseFolder}/lice/${folderStamp}/pickles_wound/lice_crop_${fileStamp}.gz .`;
}

function laksvelScore(image: ImageRow, indicator: string): number {
    return Math.round(image.indicators?.[indicator as keyof typeof image.indicators] ?? 0);
}

/** A welfare full-size image, with the URL that serves it. */
export const fullSizeImageMapper =
    (cageId: number | undefined) =>
    (image: ImageRow): MappedImage => ({
        ...image,
        path:
            apiConfig.url +
            template(WelfareController_GetFullSizeImage.key, { cageId, id: image.id }),
        scpCommand: toScpCommand(image),
    });

/** Wound detections only; the API returns every detection type on the same image. */
export const fullSizeWoundImageMapper =
    (cageId: number | undefined) =>
    (image: ImageRow): MappedImage => {
        const mapped = fullSizeImageMapper(cageId)(image);

        return {
            ...mapped,
            bboxes: mapped.bboxes?.filter((box) => ['wound', 'healing_wound'].includes(box.tag)),
        };
    };

/**
 * Strips the fin prefix so the box label reads as a bare score.
 *
 * `bboxes` is optional on the API row, and dereferencing it unguarded used to throw - which
 * useImageState's catch turned into the image being dropped from the list rather than an
 * error. This mapper is the one chosen for non-admins, so they saw fewer images on the Cage
 * Images page than admins did.
 */
export const fullSizeFinImageMapper =
    (cageId: number | undefined) =>
    (image: ImageRow): MappedImage => {
        const mapped = fullSizeImageMapper(cageId)(image);

        return {
            ...mapped,
            bboxes: mapped.bboxes?.map((box) => ({
                ...box,
                tag: box.tag.replace(FIN_TAG_PREFIX, ''),
            })),
        };
    };

/**
 * A laksvel image, carrying the rounded score for the indicator being viewed.
 *
 * Returns null for a score of 0 - there is nothing to show for it - which useImageState
 * drops. That rule used to live inside useImageState as a hardcoded
 * `image.score === 'laksvel_0'` check, in an otherwise feature-agnostic hook.
 */
export const fullSizeLaksvelImageMapper =
    (cageId: number | undefined, indicator: string) =>
    (image: ImageRow): MappedImage | null => {
        const score = laksvelScore(image, indicator);

        if (score === 0) {
            return null;
        }

        return {
            ...image,
            path:
                apiConfig.url +
                template(WelfareController_GetFullSizeImage.key, { cageId, id: image.id }),
            scpCommand: toScpCommand(image),
            score: `laksvel_${score}` as LaksvelScore,
        };
    };

/**
 * Fins are not measured the way wounds are, so the colour welfare pipeline writes this
 * placeholder into both size columns rather than leaving them empty. The legacy B&W welfare
 * store has no size columns at all and reports undefined, and `/allImages` concatenates both
 * stores, so fin boxes are normalised onto the placeholder to arrive in one shape.
 *
 * It has to be a number, not undefined: `formatBoxTooltip` drops the label of any detection
 * without a measured size, which would leave a fin box coloured but blank. Nothing renders
 * the value - the score tooltip ignores it, and the wound size filters never see a fin box.
 */
const FIN_PLACEHOLDER_SIZE_MM = 1;

/**
 * A fin status laksvel image, with the boxes reduced to the fins.
 *
 * The welfare row carries every detection found on the frame, so without the filter a fin
 * overlay would also draw the wound and lice boxes that belong to other pages. The prefix
 * is stripped for the same reason `fullSizeFinImageMapper` strips it: the box's position on
 * the fish already says which fin it is, so the label is the bare Laksvel score.
 */
export const fullSizeLaksvelFinImageMapper =
    (cageId: number | undefined, indicator: string) =>
    (image: ImageRow): MappedImage | null => {
        const mapped = fullSizeLaksvelImageMapper(cageId, indicator)(image);

        if (!mapped) {
            return null;
        }

        return {
            ...mapped,
            bboxes: mapped.bboxes
                ?.filter((box) => FIN_TAG_PREFIX.test(box.tag))
                .map((box) => ({
                    ...box,
                    tag: box.tag.replace(FIN_TAG_PREFIX, ''),
                    size_x_mm: box.size_x_mm ?? FIN_PLACEHOLDER_SIZE_MM,
                    size_y_mm: box.size_y_mm ?? FIN_PLACEHOLDER_SIZE_MM,
                })),
        };
    };

export const bioscopeImageMapper =
    (cageId: number | undefined) =>
    (bioscopeImage: BioscopeImage): MappedImage => ({
        id: bioscopeImage.id,
        timeStamp: bioscopeImage.timestamp,
        path: template(CageImageController_Image.key, { cageId, id: bioscopeImage.id }),
        ...BIOSCOPE_IMAGE_SIZE,
        bboxes: [],
    });

export const bioscopeImageBioscopeMapper =
    (bioscopeId: number | undefined) =>
    (bioscopeImage: BioscopeImage): MappedImage => ({
        id: bioscopeImage.id,
        timeStamp: bioscopeImage.timestamp,
        path: template(BioscopeImageController_Image.key, { bioscopeId, id: bioscopeImage.id }),
        ...BIOSCOPE_IMAGE_SIZE,
        bboxes: [],
    });
